<script setup lang="ts">
import {
  SelectContent,
  type SelectContentEmits,
  type SelectContentProps,
  SelectPortal,
  SelectViewport,
  useForwardPropsEmits,
} from 'radix-vue';
import { useTheme } from '@pkg/config/misc/theme';
import { cn } from '../lib/utils';

const props = withDefaults(
  defineProps<SelectContentProps & { class?: string }>(), {
    class: undefined,
    position: 'popper',
    sideOffset: 4,
    avoidCollisions: true,
  },
);
const emits = defineEmits<SelectContentEmits>();

const forwarded = useForwardPropsEmits(props, emits);
const { theme } = useTheme();
</script>

<template>
  <SelectPortal>
    <SelectContent
      v-bind="{ ...forwarded, ...$attrs }"
      :data-theme="theme"
      :class="
        cn(`
        relative
        w-[var(--radix-select-trigger-width)]
        max-w-[calc(100vw-2rem)]
        overflow-hidden
        shadow-md
        data-[state=open]:animate-in
        data-[state=closed]:animate-out
        data-[state=closed]:fade-out-0
        data-[state=open]:fade-in-0
        data-[state=closed]:zoom-out-95
        data-[state=open]:zoom-in-95
        data-[side=bottom]:slide-in-from-top-2
        data-[side=left]:slide-in-from-right-2
        data-[side=right]:slide-in-from-left-2
        data-[side=top]:slide-in-from-bottom-2
        max-h-[--radix-popper-available-height]`,
           position === 'popper'
             && `
        data-[side=bottom]:translate-y-1
        data-[side=left]:-translate-x-1
        data-[side=right]:translate-x-1
        data-[side=top]:-translate-y-1`,
           props.class,
           'z-dropdown'
        )
      "
    >
      <SelectViewport
        :class="
          cn('',
             position === 'popper'
               && `
               h-[--radix-select-trigger-height]
               w-full
               min-w-0
               bg-[color:var(--theme-background-color)]
               border
               border-[color:var(--input-primary-border)]`)"
      >
        <slot />
      </SelectViewport>
    </SelectContent>
  </SelectPortal>
</template>
